import { ClarifyingQuestions } from '@common/Copilot/CopilotAgentChat/agent-v4/v4-widgets/UserInputRequest/ClarifyingQuestionsWidget';
import { PermissionRequest } from '@common/Copilot/CopilotAgentChat/agent-v4/v4-widgets/UserInputRequest/PermissionRequestWidget';
import { theme } from '@prophecy/theme';
import { Stack } from '@prophecy/ui-v3/Layout';
import { Text } from '@prophecy/ui-v3/Typography';
import { useState } from 'react';
import styled from 'styled-components';

import { HOME_CHAT_TEST_IDS } from './constants';

import type { HomeChatElement } from './chatTranscript';
import type { SendUserInputAnswer } from '@common/Copilot/CopilotAgentChat/agent-v4/v4-widgets/UserInputRequest/ClarifyingQuestionsWidget';
import type {
  ClarifyingQuestionsProperties,
  PermissionRequestProperties
} from '@common/Copilot/CopilotAgentChat/widgetTypes';

const SectionFrame = styled(Stack)`
  width: 100%;
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.gray200};
  border-radius: ${theme.radius.x8};
  padding: ${theme.spaces.x10} ${theme.spaces.x12};
  gap: ${theme.spaces.x6};
`;

/** The same question and permission cards as the IDE chat. */
export function UserInputRequestCard({
  element,
  sendAnswer
}: {
  element: HomeChatElement;
  sendAnswer: SendUserInputAnswer;
}) {
  return element.properties?.kind === 'permission_request' ? (
    <PermissionRequest
      messageId={element.id}
      properties={element.properties as unknown as PermissionRequestProperties}
      sendAnswer={sendAnswer}
    />
  ) : (
    <ClarifyingQuestions
      messageId={element.id}
      properties={element.properties as unknown as ClarifyingQuestionsProperties}
      sendAnswer={sendAnswer}
    />
  );
}

/** Mirrors the IDE chat's UserInputRequestStickySection: one pending ask at a time, above the composer. */
export function PendingUserInput({
  requests,
  sendAnswer
}: {
  requests: HomeChatElement[];
  sendAnswer: SendUserInputAnswer;
}) {
  // Counted as answered-so-far rather than a high-water mark: a fresh ask arriving mid-run has to
  // grow the total, not push the position backwards. Resets once the queue empties.
  const [seen, setSeen] = useState({ answered: 0, remaining: requests.length });
  const answered = requests.length === 0 ? 0 : seen.answered + Math.max(0, seen.remaining - requests.length);
  if (seen.answered !== answered || seen.remaining !== requests.length) {
    setSeen({ answered, remaining: requests.length });
  }

  if (requests.length === 0) return null;
  const current = requests[0];
  const total = answered + requests.length;
  const position = answered + 1;

  return (
    <SectionFrame key={current.id} data-test-id={HOME_CHAT_TEST_IDS.pendingUserInput}>
      {total > 1 && (
        <Stack direction='horizontal' align='end'>
          <Text level='xs' tone={theme.colors.gray400}>
            {position} / {total}
          </Text>
        </Stack>
      )}
      <UserInputRequestCard element={current} sendAnswer={sendAnswer} />
    </SectionFrame>
  );
}
